import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, User, Building2, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { depotsService } from "@/services";
import { toast } from "sonner";

interface CVViewerProps {
  isOpen: boolean;
  onClose: () => void;
  cvData: {
    depotId?: string;
    candidatNom: string;
    societeNom: string;
    aoTitre: string;
    aoReference: string;
    texteExtrait: string;
    fichierFileId?: string;
    dateCreation?: string;
  } | null;
}

export const CVViewer = ({ isOpen, onClose, cvData }: CVViewerProps) => {
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');

  // Ne pas rendre le composant si cvData est null
  if (!cvData) {
    return null;
  }

  const formatCvText = (text: string) => {
    if (!text) return <div className="text-muted-foreground">Aucun contenu disponible</div>;
    
    return text.split('\n').map((line, index) => (
      <div key={index} className={`mb-2 ${line.trim() === '' ? 'mb-4' : ''}`}>
        {line.trim() === '' ? (
          <div className="h-2" />
        ) : (
          <span className={`${
            line.match(/^[A-Z][^a-z]*$/) 
              ? 'font-bold text-lg text-foreground' 
              : line.startsWith('•') || line.startsWith('-') 
                ? 'ml-4 text-muted-foreground' 
                : line.includes(':') && line.length < 50 
                  ? 'font-semibold text-foreground' 
                  : 'text-foreground'
          }`}>
            {line}
          </span>
        )}
      </div>
    ));
  };

  const downloadCV = async () => {
    if (!cvData.depotId) return;

    try {
      toast.info('Téléchargement en cours...');

      const blob = await depotsService.download(cvData.depotId);

      // Créer un URL pour le blob et déclencher le téléchargement
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CV_${cvData.candidatNom.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('CV téléchargé avec succès');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      toast.error('Erreur lors du téléchargement du CV');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-primary" />
            <span>Visualisation du CV</span>
          </DialogTitle>
        </DialogHeader>

        {/* Informations du candidat */}
        <Card className="flex-shrink-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Informations du candidat</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Candidat</div>
                  <div className="font-medium">{cvData.candidatNom}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Société</div>
                  <div className="font-medium">{cvData.societeNom}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-sm text-muted-foreground">Appel d'offres</div>
                  <div className="font-medium text-sm">
                    <Badge variant="outline">{cvData.aoReference}</Badge>
                    <div className="mt-1">{cvData.aoTitre}</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contenu du CV */}
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="flex items-center justify-between">
              <span>Contenu du CV</span>
              <Badge variant="outline" className="text-xs">
                {viewMode === 'formatted' ? 'Vue formatée' : 'Texte brut'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            <ScrollArea className="h-full w-full">
              <div className="pr-4">
                {viewMode === 'formatted' ? (
                  <div className="space-y-2">
                    {formatCvText(cvData.texteExtrait)}
                  </div>
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-foreground font-mono">
                    {cvData.texteExtrait || 'Aucun contenu disponible'}
                  </pre>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
        
        {/* Actions */}
        <div className="flex justify-between items-center flex-shrink-0 pt-4 border-t">
          <div className="flex space-x-2">
            <Button
              variant={viewMode === 'formatted' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('formatted')}
            >
              Formaté
            </Button>
            <Button
              variant={viewMode === 'raw' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('raw')}
            >
              Brut
            </Button>
          </div>
          {cvData.depotId && (
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCV}
              className="flex items-center space-x-1"
            >
              <Download className="h-4 w-4" />
              <span>Télécharger</span>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};